// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from '@sentry/nextjs';

// Skip Sentry in dev: Session Replay instruments the DOM and traces wrap every
// browser request on each navigation, adding real overhead with no benefit
// locally. Mirrors the server-side guard in instrumentation.ts.
//
// Also skip it in a production build that names itself `development`, as
// next.config.js already does for the build plugin: that is the SmartUI visual
// build (visual.yml), never a deployment. Replay there broke the capture run:
// its debounce creates timers with Sentry's own unpatched setTimeout and clears
// them with the page's clearTimeout, which Playwright's fake clock rejects
// whenever the two ids collide (#4482).
if (
  process.env.NODE_ENV === 'production' &&
  process.env.NEXT_PUBLIC_VERCEL_ENV !== 'development'
) {
  Sentry.init({
    dsn: 'https://8749bc55fe9907ce6b68344dd1a984a9@o4507906188181504.ingest.us.sentry.io/4507907542417408',

    // Add optional integrations for additional features
    integrations: [
      Sentry.replayIntegration({
        // Reduce DOM processing overhead to prevent race conditions during page transitions
        blockAllMedia: true,
        // Mask all text by default to prevent text masking race conditions
        maskAllText: true,
      }),
    ],

    // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
    tracesSampleRate: 1,

    // Define how likely Replay events are sampled.
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,

    // Define how likely Replay events are sampled when an error occurs.
    replaysOnErrorSampleRate: 1.0,

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,

    // Filter out errors that are not caused by our application code
    beforeSend(event) {
      // Filter out crypto wallet extension errors caused by environmental conflicts
      // between browser wallet extensions (MetaMask, Coinbase Wallet, etc.) and
      // third-party scripts (Usercentrics, Cloudflare Insights).
      //
      // The issue occurs when:
      // 1. A wallet extension injects its inpage.js script into the page
      // 2. Third-party scripts modify the window.ethereum object, making chainId read-only
      // 3. The wallet's script tries to update chainId and fails with a TypeError
      //
      // Since this application doesn't use web3/wallet functionality, these errors
      // are cosmetic and don't affect functionality. They should not be reported to Sentry.

      // Check if this is a wallet extension error
      const isWalletExtensionError =
        // TypeError about chainId property from wallet extension scripts
        (event.exception?.values?.[0]?.type === 'TypeError' &&
          event.exception?.values?.[0]?.value?.includes(
            'Cannot set property chainId'
          ) &&
          event.exception?.values?.[0]?.stacktrace?.frames?.some((frame) =>
            frame.filename?.includes('inpage.js')
          )) ||
        // UnhandledRejection errors from wallet extensions
        (event.exception?.values?.[0]?.type === 'UnhandledRejection' &&
          event.exception?.values?.[0]?.stacktrace?.frames?.some((frame) =>
            frame.filename?.includes('inpage.js')
          )) ||
        // Check breadcrumbs for wallet-related unhandled rejections
        (event.breadcrumbs?.some(
          (breadcrumb) =>
            breadcrumb.category === 'sentry.event' &&
            breadcrumb.message?.includes('UnhandledRejection') &&
            breadcrumb.message?.includes('promise rejection')
        ) &&
          event.exception?.values?.[0]?.stacktrace?.frames?.some((frame) =>
            frame.filename?.includes('inpage.js')
          ));

      if (isWalletExtensionError) {
        // Don't send this error to Sentry
        return null;
      }

      // Filter out Sentry Replay DOM Range errors caused by race conditions
      // during Next.js page transitions. This occurs when:
      // 1. Sentry's Replay text masking algorithm runs during page navigation
      // 2. DOM nodes get removed/detached during React hydration
      // 3. Sentry tries to call Range.selectNode() on orphaned nodes
      //
      // This is a known interaction issue between Sentry Replay's text masking
      // and dynamic DOM structures created during rapid page transitions.
      const isSentryReplayRangeError =
        event.exception?.values?.[0]?.type === 'InvalidNodeTypeError' &&
        event.exception?.values?.[0]?.value?.includes(
          "Failed to execute 'selectNode' on 'Range'"
        ) &&
        event.exception?.values?.[0]?.value?.includes('no parent');

      if (isSentryReplayRangeError) {
        // Don't send this error to Sentry
        return null;
      }

      return event;
    },
  });
}

// Export router transition hook for navigation instrumentation
export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
